// Пустой месяц в «Кому выдано» и «На что потрачено»: три разных экрана, и путать их нельзя.
//
// Правило 7 (`ПРАВИЛА.md`): пустой список это состояние с причиной и следующим действием, а не
// белое поле. Здесь три разные причины и три разных действия:
//
//  * месяц, за который просто ничего не было: причина называется, а идти можно к прошлому
//    месяцу, потому что пустым чаще всего оказывается текущий (тысячи строк майской книги лежат
//    в мае, а экран открывается на сегодняшнем месяце). Если известно, где данные есть, главная
//    кнопка ведёт прямо туда («Открыть май 2026»): на боевых данных 01.10.2026 директор,
//    листавший от сентября (там одна демонстрационная выдача) к маю, нажимал «назад» четыре раза
//    через три пустых экрана подряд;
//  * месяц, которого ещё нет: «за октябрь выдач не было» про будущий месяц было бы неправдой,
//    и идти нужно к текущему;
//  * пусто под отбором: значит снять отбор, а не листать месяцы.
//
// Месяц стоит в данных, а не в адресе: пока едет новый месяц, на экране остаётся прошлый срез.

import {
  RiArrowLeftLine,
  RiCalendarLine,
  RiFilterOffLine,
  type RemixiconComponentType,
} from '@remixicon/react';

import { formatPeriodTitle, parsePeriod, type Period } from '@/shared/time/period';
import * as Button from '@/shared/ui/button';
import type { ИмяКартинки } from '@/shared/ui/illustration';
import { EmptyState } from '@/shared/ui/screen-state';

export interface ПустойМесяцProps {
  месяц: Period;
  естьОтбор: boolean;
  /** Снять отборы по юрлицу и человеку, не трогая месяц. */
  снять: () => void;
  /** Перейти на месяц назад. */
  назад: () => void;
  /**
   * Ближайший более ранний месяц, где данные есть, если он известен. Тогда главная кнопка ведёт
   * к нему, а «К прошлому месяцу» остаётся второй: листать по одному тоже иногда нужно.
   */
  ближайший?: Period | null;
  кБлижайшему?: () => void;
  /** Вернуться на текущий месяц. */
  кТекущему: () => void;
  значок: RemixiconComponentType;
  картинка?: ИмяКартинки;
  /** Месяц прошёл (или идёт), а данных нет. Заголовок получает месяц словами, со строчной. */
  заголовокПусто: (месяцСловами: string) => string;
  описаниеПусто: string;
  заголовокОтбор: string;
  описаниеОтбор: string;
}

export function ПустойМесяц({
  месяц,
  естьОтбор,
  снять,
  назад,
  ближайший = null,
  кБлижайшему,
  кТекущему,
  значок,
  картинка,
  заголовокПусто,
  описаниеПусто,
  заголовокОтбор,
  описаниеОтбор,
}: ПустойМесяцProps) {
  if (естьОтбор) {
    return (
      <EmptyState
        icon={значок}
        title={заголовокОтбор}
        description={описаниеОтбор}
        action={
          <Button.Root variant='neutral' mode='stroke' size='xsmall' onClick={снять}>
            <Button.Icon as={RiFilterOffLine} />
            Снять отбор
          </Button.Root>
        }
      />
    );
  }

  const сейчас = parsePeriod(undefined);
  const будущий = месяц.year * 12 + месяц.month > сейчас.year * 12 + сейчас.month;
  if (будущий) {
    return (
      <EmptyState
        icon={RiCalendarLine}
        title='Этот месяц ещё не наступил'
        description={`Данных за ${formatPeriodTitle(месяц).toLowerCase()} быть не может.`}
        action={
          <Button.Root variant='neutral' mode='stroke' size='xsmall' onClick={кТекущему}>
            <Button.Icon as={RiCalendarLine} />К текущему месяцу
          </Button.Root>
        }
      />
    );
  }

  return (
    <EmptyState
      icon={значок}
      illustration={картинка}
      title={заголовокПусто(formatPeriodTitle(месяц).toLowerCase())}
      description={описаниеПусто}
      action={
        ближайший !== null && кБлижайшему !== undefined ? (
          <div className='flex flex-wrap items-center justify-center gap-2'>
            <Button.Root variant='primary' mode='filled' size='xsmall' onClick={кБлижайшему}>
              <Button.Icon as={RiCalendarLine} />
              Открыть {formatPeriodTitle(ближайший).toLowerCase()}
            </Button.Root>
            <Button.Root variant='neutral' mode='stroke' size='xsmall' onClick={назад}>
              <Button.Icon as={RiArrowLeftLine} />К прошлому месяцу
            </Button.Root>
          </div>
        ) : (
          <Button.Root variant='neutral' mode='stroke' size='xsmall' onClick={назад}>
            <Button.Icon as={RiArrowLeftLine} />К прошлому месяцу
          </Button.Root>
        )
      }
    />
  );
}
